import {
    CardTransactionTypeEnum,
    CostCenter,
    CostCenter2,
    CreditorInterface,
    DocumentTypeEnum,
    Employee,
    Expense,
    ExpenseKindEnum,
    ExpensePaymentFlowInformationEnum,
    ExpenseRequestTypeEnum,
    ExpenseStatusEnum,
    PriceIntervalEnum,
    SubscriptionTypeEnum,
    Transaction,
    Vendor,
    VendorTypeEnum,
} from "@finway-group/shared/lib/models"
import { GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { Button, Card, DatePicker, Input, Select, Tabs, Tooltip, Upload } from "antd"
import Table, { ColumnsType } from "antd/lib/table"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import { AlertTriangle, Upload as UploadIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Link, useHistory } from "react-router-dom"
import { useDebouncedCallback } from "use-debounce/lib"
import EmployeeLink from "Components/employeeLink"
import ExpenseStatusTag from "Components/expenseStatusTag"
import ExpenseTransactionStatusTag from "Components/expenseTransactionStatusTag"
import { CreditorOptionLabel } from "Components/forms/expenseForm/commonInputFields/creditorNumberDisplay"
import PriceLabel from "Components/priceLabel"
import TransactionStatusTag from "Components/transactionStatusTag"
import { filterTransactionsToDisplay } from "Features/pages/creditCards/creditCard.utils"
import { buildUnsetWorkflowUpdateData, buildWorkflowUpdateData, determineApplicableWorkflow, getActiveWorkflowRule } from "Features/pages/workflows/workflow.utils"
import WorkflowConditionTag from "Features/pages/workflows/workflowConditionTag"
import { useCompany } from "Shared/hooks/company.hooks"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useCardById } from "Shared/hooks/creditCard.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { useFilteredCreditorsForExpense, useVendors } from "Shared/hooks/vendor.hooks"
import { useWorkflows } from "Shared/hooks/workflow.hooks"
import { ExpenseAccountService, ExpenseService, NotificationService } from "Shared/services"
import { ThunkDispatchResult } from "Shared/store"
import { updateExpense } from "Shared/store/actions/expense/expenseActions"
import { setTransaction } from "Shared/store/actions/transaction/transactionActions"
import { getPopupAnchor } from "Shared/utils"
import { doFilterSelect, getTooltipPopupContainer } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { ReadOnlyContext } from "Shared/context/readOnly.context"

import { buildUpdateDataAccordingToSetting, checkForDuplicate, isMileageExpense, isPerDiemExpense, isSplitPropertiesSame } from "../expenseUtils"
import { getCostCenterLabel } from "Shared/utils/costCenter.utils"
import DeletedTooltip from "Components/deletedTooltip"

const { TabPane } = Tabs
interface ExpenseSourceSectionInterface {
    loggedInUser: Employee
    expense: Expense
    approver: Employee
    requestedBy: Employee
    costCenter?: CostCenter
    costCenter2?: CostCenter2
    vendor?: CreditorInterface
    requestType: ExpenseRequestTypeEnum | undefined
    isDeliveryNoteUploading: boolean
    isInlineEditDisabled?: boolean
    doFileUpload?: (file: any, kind: DocumentTypeEnum) => any
    setIsButtonDisabled?: React.Dispatch<React.SetStateAction<boolean>>
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    isTransactionTableLoading?: boolean
    setSelectedExpense?: (expense: Expense | undefined) => void // TODO: V check how to remove this prop. It was added to set the selected expense because when you change cc or vendor, component was not showing changes (render bug). It should work without this, as expenseRightSection does.
}

const ExpenseSourceSection: React.FC<ExpenseSourceSectionInterface> = ({
    loggedInUser,
    expense,
    approver,
    requestedBy,
    costCenter,
    costCenter2,
    vendor,
    requestType,
    isDeliveryNoteUploading,
    isInlineEditDisabled = false,
    doFileUpload,
    setIsExpenseUpdating,
    isTransactionTableLoading = false,
    setSelectedExpense,
}) => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch: ThunkDispatchResult = useDispatch()
    const { status, kind } = expense
    const isSubscription = kind === ExpenseKindEnum.SUBSCRIPTION
    const isReadOnly = React.useContext(ReadOnlyContext).isReadOnly
    const shouldShowInvoiceFields = (expense.status === ExpenseStatusEnum.INVOICE_PENDING || expense.status === ExpenseStatusEnum.APPROVED) && !expense.deleted && !isReadOnly
    const isInvoiceFieldRequired = expense.status === ExpenseStatusEnum.APPROVED && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY
    const [parentSubscription, setParentSubscription] = useStateIfMounted<Expense | undefined>(undefined)
    const [invoiceNumber, setInvoiceNumber] = useStateIfMounted(expense.invoiceNumber)
    const [invoiceDate, setInvoiceDate] = useStateIfMounted<moment.Moment | undefined | null>(expense.invoiceDate ? moment(expense.invoiceDate) : undefined)
    const [invoiceDueDate, setInvoiceDueDate] = useStateIfMounted<moment.Moment | undefined | null>(expense.invoiceDueDate ? moment(expense.invoiceDueDate) : undefined)
    const [expenseAccount, setExpenseAccount] = useStateIfMounted(expense.expenseAccount ? ExpenseAccountService.getExpenseAccountById(expense.expenseAccount) : "")
    const [datePaidAt, setDatePaidAt] = useStateIfMounted(expense.datePaidAt ? moment(expense.datePaidAt) : moment())
    const [isCostCenterUpdating, setIsCostCenterUpdating] = useStateIfMounted(false)
    const [isVendorUpdating, setIsVendorUpdating] = useStateIfMounted(false)
    const { card } = useCardById(expense.card)
    const costCenters = useSubCostCenters(true)
    const employees = useEmployees(true)
    const loggedInProfile = useLoggedInEmployeeProfile()
    const paymentTerm = vendor?.source instanceof Vendor ? vendor?.source?.rule?.paymentTerm : undefined
    const company = useCompany()
    const workflows = useWorkflows()
    const { workflow, activeRule } = getActiveWorkflowRule(expense, workflows)
    const splitsHasMissingCostCenter = expense.splits.some((s) => !s.costCenter)
    const splitsHasAnyExpenseAccount = expense.splits.some((s) => !!s.expenseAccount)
    const splitsHasAnyCostCenter2 = expense.splits.some((s) => !!s.costCenter2)
    const isUniform = isSplitPropertiesSame(expense.splits)
    const { showCostCentersIds } = useCompany()
    const vendors = useVendors(true)
    const creditors = useFilteredCreditorsForExpense(loggedInUser.activeCompanyProfile.isAdmin, expense)
    const shouldDisableDate = isMileageExpense(expense)
        ? (date: moment.Moment) => {
              return date.isAfter(moment(), "day")
          }
        : () => false

    const secondApprover = expense.secondApprovalNeededBy ? GetEmployeeById(employees, expense.secondApprovalNeededBy) : undefined

    // Filter for transactions that are not displayed in the transaction
    const displayedTransactions = useMemo(() => filterTransactionsToDisplay(expense.matchedTransactions, true), [expense])

    const showError = (err: any) => NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:request.edit.title"))
    const debouncedEventHandler = useDebouncedCallback(async (value, type) => {
        try {
            setIsExpenseUpdating?.(true)
            switch (type) {
                case "invoiceNumber":
                    if (await checkForDuplicate(expense, value)) {
                        await dispatch(updateExpense(expense.id, new Expense({ ...expense, invoiceNumber: value, skipEmail: true })))
                    } else {
                        setInvoiceNumber(expense.invoiceNumber)
                    }
                    break
                case "invoiceDate":
                    const updateData: any = new Expense({ ...expense, invoiceDate: value, skipEmail: true })
                    if (paymentTerm && value) {
                        const dueDate = moment(value).add(paymentTerm, "days")
                        if (dueDate !== invoiceDueDate) {
                            setInvoiceDueDate(dueDate)
                            updateData.invoiceDueDate = dueDate
                        }
                    }
                    if (isMileageExpense(updateData) || isPerDiemExpense(updateData)) {
                        updateData.datePurchased = updateData.invoiceDate
                    }
                    await dispatch(updateExpense(expense.id, updateData))
                    break
                case "invoiceDueDate":
                    await dispatch(updateExpense(expense.id, new Expense({ ...expense, invoiceDueDate: value, skipEmail: true })))
                    break
                case "expenseAccount":
                    await dispatch(updateExpense(expense.id, new Expense({ ...expense, expenseAccount: value, skipEmail: true })))
                    break
                case "datePaidAt":
                    await dispatch(updateExpense(expense.id, new Expense({ ...expense, datePaidAt: value, skipEmail: true })))
                    break
            }
        } catch (err) {
            showError(err)
        } finally {
            setIsExpenseUpdating?.(false)
        }
    }, 600)

    // When the component goes to be unmounted, flush the debounced input
    useEffect(() => () => debouncedEventHandler.flush(), [debouncedEventHandler])

    useEffect(() => {
        const fetchParentSubscription = async () => {
            const subscriptions = await ExpenseService.fetchSubscriptions()
            const parentSub = subscriptions.find((exp: Expense) => exp.id === expense.parentSubscription)
            setParentSubscription(parentSub)
        }
        if (isSubscription && expense.subscriptionType === SubscriptionTypeEnum.RENEWAL_REQUEST) {
            fetchParentSubscription()
        }
    }, [expense.id, expense.parentSubscription])

    useEffect(() => setInvoiceNumber(expense.invoiceNumber), [expense.invoiceNumber])
    useEffect(() => setExpenseAccount(expense.expenseAccount ? ExpenseAccountService.getExpenseAccountById(expense.expenseAccount) : undefined), [expense.expenseAccount])
    useEffect(() => setDatePaidAt(expense.datePaidAt ? moment(expense.datePaidAt) : moment()), [expense.datePaidAt])
    useEffect(() => setInvoiceDate(expense.invoiceDate ? moment(expense.invoiceDate) : undefined), [expense.invoiceDate])
    useEffect(() => setInvoiceDueDate(expense.invoiceDueDate ? moment(expense.invoiceDueDate) : undefined), [expense.invoiceDueDate])

    const onVendorUpdate = async (value: string) => {
        try {
            setIsVendorUpdating(true)
            setIsExpenseUpdating?.(true)
            const selectedCreditor = creditors.find((c) => c.id === value)
            const expenseWithNewVendor = new Expense({ ...expense, vendor: value })
            const workflow = determineApplicableWorkflow(expenseWithNewVendor, workflows, true, loggedInUser.settings.showGrossAmount)
            const workflowUpdate = workflow ? buildWorkflowUpdateData(expenseWithNewVendor, workflow) : buildUnsetWorkflowUpdateData()
            expense.vendor = value
            const updateData = buildUpdateDataAccordingToSetting(expense, vendors, employees, company)

            // Adjust splits. Rules applied by changing vendor here should overwrite the splits.
            for (const split of expense.splits) {
                if (updateData.costCenter) split.costCenter = updateData.costCenter
            }

            await dispatch(
                updateExpense(
                    expense.id,
                    new Expense({
                        ...expense,
                        skipEmail: true,
                        ...updateData,
                        ...workflowUpdate,
                        vendorType: selectedCreditor?.type,
                    })
                )
            )
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsVendorUpdating(false)
            setIsExpenseUpdating?.(false)
        }
    }

    const onCostCenterUpdate = async (value: string) => {
        try {
            setIsCostCenterUpdating(true)
            setIsExpenseUpdating?.(true)
            const expenseWithNewCC = new Expense({ ...expense, costCenter: value })
            const workflow = determineApplicableWorkflow(expenseWithNewCC, workflows, true, loggedInUser.settings.showGrossAmount)
            const workflowUpdate = workflow ? buildWorkflowUpdateData(expenseWithNewCC, workflow) : buildUnsetWorkflowUpdateData()
            const updatedExpense = new Expense({
                ...expense,
                costCenter: value,
                skipEmail: true,
                ...workflowUpdate,
            })
            await dispatch(updateExpense(expense.id, updatedExpense))
            setSelectedExpense?.(updatedExpense)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsCostCenterUpdating(false)
            setIsExpenseUpdating?.(false)
        }
    }

    const VendorDetails = ({ vendor }: { vendor: CreditorInterface }) => {
        const redirectDestination = vendor?.type === "User" ? "employees" : "vendors"
        const VendorLink = <Link to={`/${redirectDestination}/${vendor.id}`}>{vendor.name}</Link>
        return (
            <div>
                {!!vendor.creditorNumber ? (
                    <>
                        <Link className="ml-4" to={`/${redirectDestination}/${vendor.id}`}>
                            {vendor.name}
                        </Link>
                        {vendor?.source?.deleted && <DeletedTooltip translationKey="tooltips:vendor.was_deleted" deletionDate={vendor?.source?.dateDeleted} />}
                    </>
                ) : (
                    <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:employee_no_creditor_number")}>
                        <div className="flex justify-end">
                            <AlertTriangle className="px-2 mt-2" fill="#FBAC3B" size={18} stroke="#FFFFFF" />
                            {VendorLink}
                        </div>
                    </Tooltip>
                )}
            </div>
        )
    }

    const getPurchaseData = () => {
        const isAdmin = loggedInUser.activeCompanyProfile.isAdmin
        const isCostCenterApprover = loggedInUser.activeCompanyProfile.isApprover && loggedInUser.id === costCenter?.responsibleUser
        const isRequester = loggedInUser.id === expense.requestedBy
        const isApprover = loggedInUser.id === expense.approvalNeededBy
        const isTeamApprover = loggedInUser.activeCompanyProfile.isApprover && loggedInUser.activeCompanyProfile.team === requestedBy.team

        return (
            <ul className="description-list pb-10" id="expense-purchase-data-list">
                <li>
                    <span className="title">{t("label:status")}</span>
                    <span className="text">
                        <ExpenseStatusTag status={status} />
                    </span>
                </li>
                <li>
                    <span className="title">{t("label:purchased")}</span>
                    <span className="text">{moment(expense.datePurchased).format("ll")}</span>
                </li>
                {expense.performancePeriodStartDate &&
                    (expense.performancePeriodStartDate === expense.performancePeriodEndDate ? (
                        <li>
                            <span className="title">
                                <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:cannot_be_transferred_to_datev")}>
                                    {t("label:performance_date")}*
                                </Tooltip>
                            </span>
                            <span className="text">{moment(expense.performancePeriodStartDate).format("ll")}</span>
                        </li>
                    ) : (
                        <>
                            <li>
                                <span className="title">
                                    <Tooltip getPopupContainer={getTooltipPopupContainer} placement="bottom" title={t("info:cannot_be_transferred_to_datev")}>
                                        {t("label:performance_period_start_date")}*
                                    </Tooltip>
                                </span>
                                <span className="text" style={{ marginLeft: "10px", minWidth: "85px" }}>
                                    {moment(expense.performancePeriodStartDate).format("ll")}
                                </span>
                            </li>
                            <li>
                                <span className="title">
                                    <Tooltip getPopupContainer={getTooltipPopupContainer} placement="bottom" title={t("info:cannot_be_transferred_to_datev")}>
                                        {t("label:performance_period_end_date")}*
                                    </Tooltip>
                                </span>

                                <span className="text" style={{ marginLeft: "10px", minWidth: "85px" }}>
                                    {moment(expense.performancePeriodEndDate).format("ll")}
                                </span>
                            </li>
                        </>
                    ))}
                <li>
                    <span className="title">{t("label:requested_by")}</span>
                    <span className="text truncate max-w-250">
                        <EmployeeLink employee={requestedBy} />
                    </span>
                </li>
                <li>
                    <span className="title">{t("label:approver")}</span>
                    <span className="text truncate max-w-250">
                        <EmployeeLink employee={approver} />
                    </span>
                </li>
                {secondApprover && (
                    <li>
                        <span className="title">{t("label:second_approver")}</span>
                        <span className="flex flex-row truncate max-w-250">
                            <WorkflowConditionTag condition={activeRule?.condition} />
                            <EmployeeLink employee={secondApprover} />
                        </span>
                    </li>
                )}
                {workflow && (
                    <li>
                        <span className="title">{t("label:workflow.workflow")}</span>
                        <span className="text truncate max-w-250">{isAdmin ? <Link to={`/workflows/${workflow._id}`}>{workflow.name}</Link> : workflow.name}</span>
                    </li>
                )}
                <li>
                    <span className="title">{t("label:cost_center")}</span>
                    {(isAdmin || isCostCenterApprover || isRequester || isApprover || isTeamApprover) && costCenter && isUniform.costCenter ? (
                        <span className="text truncate max-w-250">
                            {isAdmin || isCostCenterApprover ? <Link to={`/costCenters/${costCenter._id}`}>{costCenter.name}</Link> : costCenter.name}
                            {costCenter?.deleted && <DeletedTooltip translationKey="tooltips:cost_center.was_deleted" deletionDate={costCenter?.dateDeleted} />}
                        </span>
                    ) : !isInlineEditDisabled && splitsHasMissingCostCenter ? (
                        <Select
                            onSelect={onCostCenterUpdate}
                            className="w-1/2"
                            showSearch
                            filterOption={doFilterSelect}
                            getPopupContainer={getPopupAnchor()}
                            defaultValue=""
                            loading={isCostCenterUpdating}
                            disabled={isCostCenterUpdating || isReadOnly}
                        >
                            <Select.Option value="" disabled>
                                <div className="flex items-center overflow-hidden">
                                    <span>-</span>
                                </div>
                            </Select.Option>
                            {costCenters.map((cc, index) => (
                                <Select.Option key={index} value={cc._id} id={`costcenter-option-${index}`} label={getCostCenterLabel(cc, showCostCentersIds)}>
                                    {getCostCenterLabel(cc, showCostCentersIds)}
                                </Select.Option>
                            ))}
                        </Select>
                    ) : (
                        <span className="text truncate">{isUniform.costCenter && costCenter ? costCenter?.name : expense.splits.length > 1 ? t("label:splitted") : "-"}</span>
                    )}
                </li>
                {(costCenter2 || (expense.splits.length > 1 && splitsHasAnyCostCenter2)) && (
                    <li>
                        <span className="title">{t("label:cost_center_2")}</span>
                        <span className="text truncate max-w-250">{isUniform.costCenter2 && costCenter2 ? costCenter2.name : t("label:splitted")}</span>
                    </li>
                )}
                {(expenseAccount || (expense.splits.length > 1 && splitsHasAnyExpenseAccount)) && (
                    <li>
                        <span className="title">{t("label:expense_account")}</span>
                        <span className="text ml-16">
                            <p className="truncate">
                                {expenseAccount && isUniform.expenseAccount ? `${expenseAccount.accountCode} - ${expenseAccount.accountName}` : t("label:splitted")}
                            </p>
                        </span>
                    </li>
                )}
                <li>
                    <span className="title">{t("label:vendor")}</span>
                    {vendor && (vendor.type !== VendorTypeEnum.VENDOR || (vendor.source as Vendor)?.isApproved) ? (
                        <VendorDetails vendor={vendor} />
                    ) : !isInlineEditDisabled && !isReadOnly ? (
                        <Select
                            onSelect={onVendorUpdate}
                            className="w-1/2"
                            showSearch
                            filterOption={doFilterSelect}
                            getPopupContainer={getPopupAnchor()}
                            value={vendor ? vendor.id : ""}
                            loading={isVendorUpdating}
                            disabled={isReadOnly || isVendorUpdating}
                        >
                            <Select.Option value="" disabled>
                                <div className="flex items-center overflow-hidden">
                                    <span>-</span>
                                </div>
                            </Select.Option>
                            {creditors.map((creditor, index) => (
                                <Select.Option key={index} value={creditor.id} label={creditor.name} id={`vendor-option-${index}`}>
                                    <CreditorOptionLabel creditor={creditor} />
                                </Select.Option>
                            ))}
                        </Select>
                    ) : (
                        <span className="text truncate">-</span>
                    )}
                </li>
                {card && (
                    <li>
                        <span className="title">{t("label:attached_card")}</span>
                        <span className="text truncate max-w-300">
                            <Link to={`/credit-cards/${card?._id}`}>{`**** **** **** ${card?.last4Digits}`}</Link>
                        </span>
                    </li>
                )}
                {isSubscription && expense.subscriptionType === SubscriptionTypeEnum.RENEWAL_REQUEST && (
                    <li>
                        <span className="title">{t("label:subscription")}</span>
                        <span className="text">
                            <Link to={`/subscriptions/${parentSubscription?.id}`}>{parentSubscription?.name}</Link>
                        </span>
                    </li>
                )}
                {expense.paymentFlowInformation && (
                    <li>
                        <span className="title">{t("label:payment_flow_information.title")}</span>
                        <span className="text">
                            <span>{t(`label:payment_flow_information.${expense.paymentFlowInformation}`)}</span>
                        </span>
                    </li>
                )}
                {requestType !== ExpenseRequestTypeEnum.PURCHASE && (
                    <React.Fragment>
                        {shouldShowInvoiceFields ? (
                            <li>
                                <span className={`w-1/2 title ${isInvoiceFieldRequired && "label-required"}`}>
                                    {isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_no") : t("label:invoice_no")}
                                </span>
                                <Input
                                    className="w-1/2"
                                    placeholder="Invoice number"
                                    value={invoiceNumber}
                                    onChange={(event) => {
                                        setInvoiceNumber(event.target.value)
                                        debouncedEventHandler.callback(event.target.value, "invoiceNumber")
                                        if (!event.target.value) debouncedEventHandler.flush()
                                    }}
                                    onBlur={(_event) => debouncedEventHandler.flush()}
                                />
                            </li>
                        ) : (
                            expense.invoiceNumber && (
                                <li>
                                    <span className="title">{isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_no") : t("label:invoice_no")}</span>
                                    <span className="text">{expense.invoiceNumber}</span>
                                </li>
                            )
                        )}
                        {shouldShowInvoiceFields ? (
                            <li>
                                <span className={`w-1/2 title ${isInvoiceFieldRequired && "label-required"}`}>
                                    {isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_date") : t("label:invoice_date")}
                                </span>
                                <DatePicker
                                    className="w-1/2"
                                    value={invoiceDate}
                                    format={moment.localeData().longDateFormat("l")}
                                    placeholder={t("placeholder:request.date")}
                                    onChange={(value) => {
                                        setInvoiceDate(value)
                                        debouncedEventHandler.callback(value ? value.toDate() : value, "invoiceDate")
                                        if (!value) debouncedEventHandler.flush()
                                    }}
                                    onBlur={(_event) => debouncedEventHandler.flush()}
                                    getPopupContainer={getPopupAnchor()}
                                    disabledDate={shouldDisableDate}
                                />
                            </li>
                        ) : (
                            expense.invoiceDate && (
                                <li>
                                    <span className="title">
                                        {isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_date") : t("label:invoice_date")}
                                    </span>
                                    <span className="text">{moment(expense.invoiceDate).format("ll")}</span>
                                </li>
                            )
                        )}
                        {shouldShowInvoiceFields ? (
                            <li>
                                <span className={`w-1/2 title `}>{t("label:invoice_due_date")}</span>
                                <DatePicker
                                    className="w-1/2"
                                    value={invoiceDueDate}
                                    format={moment.localeData().longDateFormat("l")}
                                    placeholder={t("placeholder:request.date")}
                                    onChange={(value) => {
                                        setInvoiceDueDate(value)
                                        debouncedEventHandler.callback(value ? value.toDate() : value, "invoiceDueDate")
                                        if (!value) debouncedEventHandler.flush()
                                    }}
                                    onBlur={(_event) => debouncedEventHandler.flush()}
                                    getPopupContainer={getPopupAnchor()}
                                />
                            </li>
                        ) : (
                            expense.invoiceDueDate && (
                                <li>
                                    <span className="title">{t("label:invoice_due_date")}</span>
                                    <span className="text">{moment(expense.invoiceDueDate).format("ll")}</span>
                                </li>
                            )
                        )}
                        {expense.isReviewed() && isAdmin ? (
                            <li>
                                <span className={`w-1/2 title label-required`}>{t("label:date_paid_at")}</span>
                                <DatePicker
                                    className="w-1/2"
                                    value={datePaidAt}
                                    format={moment.localeData().longDateFormat("l")}
                                    placeholder={t("placeholder:request.date")}
                                    onChange={(value) => {
                                        setDatePaidAt(value ?? moment())
                                        debouncedEventHandler.callback(value ? value.toDate() : undefined, "datePaidAt")
                                        if (!value) debouncedEventHandler.flush()
                                    }}
                                    onBlur={(_event) => debouncedEventHandler.flush()}
                                    getPopupContainer={getPopupAnchor()}
                                />
                            </li>
                        ) : (
                            expense.isSuccessfullyProcessed() &&
                            expense.datePaidAt && (
                                <li>
                                    <span className="title">
                                        {moment(expense.datePaidAt).startOf("day").isAfter(moment()) ? t("label:to_be_paid_at") : t("label:date_paid_at")}
                                    </span>
                                    <span className="text">{moment(expense.datePaidAt).format("ll")}</span>
                                </li>
                            )
                        )}
                        {expense.matchedTransactions?.length > 0 && loggedInUser.activeCompanyProfile?.isAdmin && (
                            <li>
                                <span className="title">{t("label:matching.transaction_status")}</span>
                                <span className="text">
                                    <ExpenseTransactionStatusTag expense={expense} />
                                </span>
                            </li>
                        )}
                        {!isSubscription && doFileUpload !== undefined && !isReadOnly && expense.kind !== ExpenseKindEnum.MILEAGE && expense.kind !== ExpenseKindEnum.PER_DIEM && (
                            <li>
                                <span className="grid-breakpoints hidden title xs:block xs:w-1/2 sm:hidden xl:block xl:w-1/2">{t("label:delivery_note")}</span>
                                <span className="grid-breakpoints w-full xs:w-1/2 sm:w-full xl:w-1/2 ant-btn p-0">
                                    <Upload
                                        customRequest={(req) => {
                                            doFileUpload(req.file, DocumentTypeEnum.DELIVERY_NOTE)
                                        }}
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        showUploadList={false}
                                    >
                                        <Button loading={isDeliveryNoteUploading} disabled={expense.deleted} type="text" className="bg-transparent shadow-none">
                                            <UploadIcon />
                                            <span>{expense.deliveryNote ? t("action:replace_delivery_note") : t("action:upload_delivery_note")}</span>
                                        </Button>
                                    </Upload>
                                </span>
                            </li>
                        )}
                    </React.Fragment>
                )}
            </ul>
        )
    }

    const getVendorData = () => {
        if (!vendor) return <></>

        if (vendor.source instanceof Vendor) {
            const expenseVendor = vendor.source
            let locationLbl = ""
            if (!expenseVendor.city && !expenseVendor.zip && !expenseVendor.country) locationLbl = "-"
            else if (expenseVendor.city && expenseVendor.country)
                locationLbl = `${expenseVendor.city} ${expenseVendor.zip ? `(${expenseVendor.zip}` : ""}, ${expenseVendor.country}`
            else if (expenseVendor.country) locationLbl = expenseVendor.country

            return (
                <ul className="description-list pb-10 overflow-auto">
                    <li>
                        <span className="title">{t("label:name")}</span>
                        <span className="text truncate max-w-250">
                            <Link to={`/vendors/${expenseVendor.id}`}>{expenseVendor.name}</Link>
                        </span>
                    </li>
                    <li>
                        <span className="title">{t("label:creditor_number")}</span>
                        <span className="text truncate max-w-250">{expenseVendor.creditorNumber ?? "-"}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:description")}</span>
                        <span className="text truncate max-w-250">{expenseVendor.description}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:location")}</span>
                        <span className="text truncate max-w-250">{locationLbl}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:vat_number")}</span>
                        <span className="text">{expenseVendor.vatNumber || "-"}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:iban")}</span>
                        <span className="text">{expenseVendor.iban || "-"}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:bic")}</span>
                        <span className="text">{expenseVendor.bic || "-"}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:contact_person")}</span>
                        <span className="text truncate max-w-250">{expenseVendor.contactPersonName || "-"}</span>
                    </li>
                </ul>
            )
        }
        const employee = vendor.source
        return (
            <ul className="description-list pb-10 overflow-auto">
                <li>
                    <span className="title">{t("label:name")}</span>
                    <span className="text truncate max-w-250">
                        <Link to={`/employees/${employee.id}`}>{employee.getFullName()}</Link>
                    </span>
                </li>
                <li>
                    <span className="title">{t("label:location")}</span>
                    <span className="text truncate max-w-250">{employee.location}</span>
                </li>
                <li>
                    <span className="title">{t("label:iban")}</span>
                    <span className="text">{vendor.iban || "-"}</span>
                </li>
                <li>
                    <span className="title">{t("label:bic")}</span>
                    <span className="text">{vendor.bic || "-"}</span>
                </li>
                {loggedInProfile.activeCompanyProfile.isAdmin && (
                    <li>
                        <span className="title">{t("label:creditor_number")}</span>
                        <span className="text truncate max-w-250">{vendor.creditorNumber || "-"}</span>
                    </li>
                )}
            </ul>
        )
    }

    const transactionColumns: ColumnsType<any> = [
        {
            key: "transactionData.date",
            ellipsis: true,
            sorter: true,
            align: "center",
            render: ({ transactionData }: Transaction) => <span>{moment(transactionData?.date).format("Do MMM, YYYY")}</span>,
        },
        {
            key: "transactionData.amount",
            ellipsis: true,
            align: "center",
            render: ({ transactionData }: Transaction) => (
                <PriceLabel value={transactionData?.amount} currency={transactionData?.currency || expense.currency} interval={PriceIntervalEnum.ONE_TIME} addPlusSign />
            ),
        },
        {
            key: "status",
            title: t("label:status"),
            ellipsis: true,
            render: ({ status, cardData }: Transaction) => (
                <TransactionStatusTag status={status} isReversal={cardData?.cardTransactionType === CardTransactionTypeEnum.ONLINE_REVERSE} />
            ),
        },
    ]

    const getTransactionData = () => (
        <Table
            showHeader={false}
            loading={isTransactionTableLoading}
            rowKey={({ _id }: any) => _id}
            dataSource={displayedTransactions}
            pagination={false}
            columns={transactionColumns}
            onRow={(transaction) => {
                return {
                    onClick: () => {
                        dispatch(setTransaction(transaction))
                        history.push(`/transactions/${transaction._id}`)
                    },
                }
            }}
        />
    )

    return (
        <Card className="mt-20 card-with-tabs" id="expense-source-section">
            <Tabs defaultActiveKey="1" className="mb-8">
                <TabPane key="purchaseData" tab={t("label:purchase_data")}>
                    {getPurchaseData()}
                </TabPane>

                {vendor && (
                    <TabPane key="vendorData" tab={t("label:vendor_data")}>
                        {getVendorData()}
                    </TabPane>
                )}
                {displayedTransactions.length > 0 && (
                    <TabPane key="transactionData" tab={t("label:transaction_data")}>
                        {getTransactionData()}
                    </TabPane>
                )}
            </Tabs>
        </Card>
    )
}

export default ExpenseSourceSection
